/**
 * نصوص الصفحات التحريريّة بثلاث لغات (en / ar / fr).
 *
 * كل عنوان أو فقرة يراها الزائر في الصفحات الثابتة (About, Journal,
 * Contact, Gifts, Shipping, Returns, Privacy, Terms, Home sections, SEO meta)
 * تُكتب هنا. تعديل أيّ نصّ لاحقاً = سطر واحد بثلاث لغات في هذا الملفّ.
 *
 * يُدمج تلقائياً مع قواميس i18n.ts عبر `mergeContentDicts()`.
 */

import type { Locale } from "@/lib/i18n";

type Dict = Record<string, string>;

// ─────────────────────────────────────────────
// EN
// ─────────────────────────────────────────────
const en: Dict = {
  "driver.delivery_event_retry": "Reconcile delivery event",
  "driver.delivery_event_result": "Delivery event: {result}",
  "header.contextual_currency": "Prices in {currency}.",
  "header.currency_pending": "Currency not confirmed",
  "product.delivery_at_checkout": "Delivery availability and timing confirmed at checkout.",
  "cart.subtotal": "Subtotal",
  "cart.money_pending": "Refreshing prices",
  "cart.free_shipping.remaining": "Add {remaining} in products to qualify for free shipping.",
  "cart.free_shipping.eligible": "Your order qualifies for free shipping.",
  "cart.free_shipping.condition": "Free shipping on a merchandise subtotal of {remaining} or more after discounts, before tax.",
  "cart.free_shipping.destination": "For delivery to {country}. Eligibility is confirmed at checkout after discounts; shipping and taxes do not count toward the threshold.",
  "cart.payment_options_domestic": "Apple Pay · Cards · Tabby · Tamara",
  "cart.payment_options_checkout": "Available payment options confirmed at checkout",
  "cart.market_recheck_selected": "Re-check bag for {country}",
  "cart.checkout_context_blocked": "The saved checkout destination could not be safely reset. Checkout remains blocked. Empty this bag and add your items again for the selected country; your gift destination will not be changed automatically.",
  "cart.quoted_shipping": "Shipping estimate",
  "cart.checkout_totals_notice": "Shipping, taxes and the final total are confirmed at checkout.",
  "driver.login.title": "Driver sign in",
  "driver.login.email": "Email",
  "driver.login.password": "Password",
  "driver.login.submit": "Sign in",
  "driver.login.error": "Unable to sign in. Check your details and connection.",
  "recovery.email_greeting": "Hello,",
  "recovery.email_selection": "Your selection",
  "recovery.email_consent": "I agree to receive email reminders about this account’s cart before checkout. I can withdraw this consent here at any time.",
  "recovery.saved": "Email recovery consent saved",
  "recovery.save": "Save consent",
  "recovery.saving": "Saving…",
  "recovery.withdraw": "Withdraw consent",
  "recovery.error": "Consent could not be saved. Please try again.",
  "representative.title": "Qatar Representative",
  "representative.brand": "Lecmo Perfume",
  "representative.language": "العربية",
  "representative.status.pending": "Pending",
  "representative.status.assigned": "Assigned",
  "representative.status.picked_up": "Picked up",
  "representative.status.out_for_delivery": "Out for delivery",
  "representative.status.delivered": "Delivered",
  "representative.status.failed": "Failed",
  "representative.status.returned": "Returned",
  "representative.status.paid": "Paid",
  "representative.status.authorized": "Authorized",
  "representative.status.partially_paid": "Partially paid",
  "representative.status.refunded": "Refunded",
  "representative.status.partially_refunded": "Partially refunded",
  "representative.status.voided": "Voided",
  "representative.status.unpaid": "Unpaid",
  "representative.admin": "Representatives",
  "representative.orders": "Qatar delivery orders",
  "representative.loading": "Checking access…",
  "representative.signin": "Sign in",
  "representative.signinRequired": "Sign in to your representative account",
  "representative.setup_pending": "Access setup pending",
  "representative.forbidden": "Access unavailable for this account or order",
  "representative.unavailable": "Orders are temporarily unavailable",
  "representative.empty": "No Qatar delivery orders",
  "representative.details": "Order details",
  "representative.recipient": "Delivery recipient",
  "representative.items": "Order items",
  "representative.delivery": "Delivery status",
  "representative.financial": "Financial status",
  "representative.previous": "Previous",
  "representative.next": "Next",
  "representative.page": "Page {page}",
  "representative.refresh": "Refresh",
  "representative.back": "Back to orders",
  "representative.unknown": "Not recorded",
  "representative.ready": "Mapping ready",
  "representative.setupTitle": "Qatar access setup",
  "representative.enabledMappings": "Enabled mappings: {count}",
  "representative.setupNotice": "Account access and country routing require separate authorization. No access can be activated from this page.",
  "representative.open": "Open representative page",
  "representative.meta.description": "Private Qatar delivery orders for authorized Lecmo representatives.",
  "representative.admin.description": "Private representative access setup overview for Lecmo administrators.",
  "cart.import_charges_notice": "For international shipping: Destination customs duties and import taxes may apply and are not included in the price unless stated otherwise at checkout.",
  // ── إذن إشعارات المتصفّح (soft prompt) ──
  "push.optin.title": "Never miss a new release",
  "push.optin.body":
    "Turn on browser notifications and we'll let you know about new launches, restocks and your order updates. No email needed.",
  "push.optin.allow": "Turn on notifications",
  "push.optin.later": "Not now",
  "push.optin.enabled": "Notifications are on.",
  "push.optin.blocked": "Notifications are blocked in your browser settings.",
  // ── Home desktop editorial (blocks rebuilt for desktop) ──
  "home.d.marquee": "Discover the Lecmo collection — handcrafted perfumes, delivered across the Gulf",
  "home.d.statement.line1": "Some scents you wear.",
  "home.d.statement.line2": "Some scents remember you.",
  "home.d.statement.body":
    "Every Lecmo fragrance begins as a memory: a courtyard at first light, oud smoke in a winter room, a night in the desert that never quite ended. Composed slowly, bottled in small batches, made to be worn often.",
  "home.d.statement.cta": "Behind the house",
  "home.d.spotlight.inside": "What's inside",
  "home.d.spotlight.cta": "View the product",
  "home.d.spotlight.choose": "Choose your set",
  "home.d.spotlight.upgrade": "Upgrade",
  "home.d.spotlight.add": "Add",
  "home.d.fragrances.title": "The Fragrances",
  "home.d.fragrances.all": "Explore all",
  "home.d.outro.caption": "It's the tale of a wolf — it's the tale of a fragrance.",

  // ── Hero video toggle ──
  "hero.video.pause": "Pause background video",
  "hero.video.play": "Play background video",
  // ── Home (sections H2 — كانت hardcoded) ──
  "home.featured.h2": "A composed selection",
  "home.editorPicks.eyebrow": "From the house",
  "home.editorPicks.title": "What we're wearing now",
  "home.editorPicks.cta": "Discover the scent",
  "home.gifts.h2": "Wrapped like a secret.",
  "home.gifts.body":
    "A handwritten note, a velvet-lined box, delivered on the day that matters.",
  "home.story.h2": "Composed like a poem.",
  "home.about.eyebrow": "About Lecmo",
  "home.about.h2": "A house of perfume, \nwritten in the language of the Gulf.",
  "home.about.body":
    "\n",
  "home.about.cta": "Read our story",
  "home.ambassador.eyebrow": "Lecmo Ambassador Program",
  "home.ambassador.h2": "Earn While You Inspire",
  "home.ambassador.body": "Join the Lecmo Ambassador program and earn from every order made through your link.",
  "home.ambassador.cta": "Become an Ambassador",
  "home.editorial.h2": "From the Journal",
  "home.editorial.post1.title": "The Language of Rose",
  "home.editorial.post1.excerpt": "Why a single petal can carry a memory.",
  "home.editorial.post2.title": "On Oud and Patience",
  "home.editorial.post2.excerpt": "Aging the resin that defines an empire.",
  "home.editorial.post3.title": "A Drop, A Ritual",
  "home.editorial.post3.excerpt": "How to wear a fragrance, slowly.",
  "home.collections.aria": "Collections",
  "section.collections": "Fragrance Collections",
  "cta.discover": "Discover",
  "common.back": "Back",
  // ── Body Care Set (LECMO × NEMRO) ──
  "bodyCare.eyebrow": "Exclusive Collaboration",
  "bodyCare.title": "LECMO × NEMRO",
  "bodyCare.subtitle": "A ritual of care, composed like a fragrance.",
  "bodyCare.p1":
    "This is where perfume meets the skin before the first drop is worn. A limited body-care collection born from a shared obsession with texture, slowness, and the quiet ceremony of getting ready.",
  "bodyCare.p2":
    "Each formula is being composed as carefully as a Lecmo fragrance — clean notes, rich lather, and a lingering softness that feels like a secret left on the skin.",
  "bodyCare.signature": "Coming soon. Join the waitlist to be the first to know.",
  "bodyCare.alt": "LECMO × NEMRO body care collection — coming soon",

  "collection.card.default_desc": "Discover this Lecmo creation.",
  "home.meta.title": "Lecmo — Luxury Fragrances & Gift Sets",
  "home.meta.description":
    "LECMO is a luxury fragrance story where refined taste meets emotion and fragrance artistry is born—through distinctive perfumes and elegant gift sets.",
  "product.seo.the-blue-oud.title": "The Blue Oud — Luxury Oud Eau de Parfum | Lecmo UAE",
  "product.seo.the-blue-oud.description":
    "The Blue Oud by Lecmo — a rich oriental oud Eau de Parfum crafted in the UAE. Gift-ready packaging, delivered across the Emirates.",
  "product.seo.the-pink.title": "The Pink — Elegant Eau de Parfum | Lecmo UAE",
  "product.seo.the-pink.description":
    "The Pink by Lecmo — an elegant Eau de Parfum crafted in the UAE. A gift-worthy bottle, delivered across the Emirates.",
  "product.seo.the-blue.title": "The Blue — Signature Eau de Parfum | Lecmo UAE",
  "product.seo.the-blue.description":
    "The Blue by Lecmo — a signature Eau de Parfum crafted in the UAE. Gift-ready packaging, delivered across the Emirates.",
  "newproduct.meta.title": "New Releases — Lecmo | Latest Fragrance Launches",
  "newproduct.meta.description":
    "Discover Lecmo's newest compositions: fresh launches with their notes, inspiration, and cinematic presentation.",
  "newproduct.index.title": "New Releases",
  "newproduct.index.subtitle": "Each new composition has its own page.",
  "newproduct.index.empty": "No new releases yet.",
  "newproduct.index.view": "Discover",

  // ── About ──
  "about.eyebrow": "The House",
  "about.title": "A house of quiet poems.",
  "about.subtitle": "Born in the Gulf, composed for those who listen to scent.",
  "about.image_alt": "The atelier",
  "about.cta": "Explore our story",
  "about.p1":
    "We began in a small atelier between two cities — one ancient, one electric — with the belief that a perfume is not a product but a private memory you carry on the skin.",
  "about.p2":
    "Each composition begins with a feeling: the hush of a velvet curtain, the gold of a late afternoon, the warmth of someone you love returning home. We translate these into notes — top, heart, and base — and patience does the rest.",
  "about.p3":
    "Our raw materials are sourced from perfumers we know by name, in fields we have walked. Roses from Taif. Saffron from the highlands of Iran. Oud aged in cedar for seven years. Vanilla cured slowly in Madagascar.",
  "about.p4":
    "And every bottle leaves our atelier wrapped as if it were a secret. Because it is.",
  "about.meta.title": "The House — Lecmo | Our Story & Craft",
  "about.meta.description":
    "Meet the house behind Lecmo: our atelier, our perfumers, and the craft that shapes every composition.",

  // ── About: editorial sections ──
  "about.who.eyebrow": "Who is Lecmo",
  "about.who.title": "An independent Emirati perfume house.",
  "about.who.body":
    "Lecmo creates fragrances and scented gifts with a luxury that never raises its voice — considered compositions, distinctive design, and an unboxing moment made to be remembered.",
  "about.story.eyebrow": "Our story",
  "about.story.title": "It started with one question.",
  "about.story.body":
    "What if a fragrance could be read like a personality instead of worn like a trend? Lecmo began in the UAE around that idea: a small house, few compositions, no compromise — built for people who choose scent slowly and keep it for years.",
  "about.philosophy.eyebrow": "Philosophy",
  "about.philosophy.title": "Quiet luxury, honestly made.",
  "about.philosophy.body":
    "We want your fragrance to say something true about you. Balanced compositions, a design that carries feeling as much as taste, and a presence that lingers without shouting.",
  "about.diff.eyebrow": "What sets us apart",
  "about.diff.title": "Details you can feel.",
  "about.diff.1.title": "Composition quality",
  "about.diff.1.body": "Concentrated oils, long maceration, and a fixed formula we don't quietly change.",
  "about.diff.2.title": "Distinctive design",
  "about.diff.2.body": "Bottles and cases designed as objects worth keeping on display.",
  "about.diff.3.title": "The unboxing",
  "about.diff.3.body": "Layered packaging, considered weight, and a moment of anticipation before the scent.",
  "about.diff.4.title": "Gift collections",
  "about.diff.4.body": "Ready gift sets for occasions, with a card and a market-aware presentation.",
  "about.diff.5.title": "Bakhoor pairing",
  "about.diff.5.body": "Selected collections arrive with bakhoor composed to echo the fragrance at home.",
  "about.craft.eyebrow": "How we compose",
  "about.craft.title": "Four steps, no shortcuts.",
  "about.craft.1.title": "Selecting materials",
  "about.craft.1.body": "We choose oils and raw materials by character, not by cost.",
  "about.craft.2.title": "Building the notes",
  "about.craft.2.body": "Top, heart, and base are drawn as a structure before anything is mixed.",
  "about.craft.3.title": "Trials",
  "about.craft.3.body": "Rounds of testing on skin, across hours and climates, until the story holds.",
  "about.craft.4.title": "A stable balance",
  "about.craft.4.body": "The final formula is locked, so the bottle you love smells the same next year.",
  "about.identity.eyebrow": "Emirati roots, global view",
  "about.identity.title": "From the UAE, outward.",
  "about.identity.body":
    "Lecmo is made in the Emirates and speaks in the language of the Gulf — with an ambition that reaches the wider Gulf and, in time, the world.",
  "about.values.eyebrow": "Our values",
  "about.values.1": "Authenticity",
  "about.values.2": "Creativity",
  "about.values.3": "Quality",
  "about.values.4": "Attention to detail",
  "about.values.5": "Respect for your taste",
  "about.closing.title": "Discover the scent that resembles you.",
  "about.closing.body": "Begin your journey with Lecmo — one fragrance, chosen slowly.",
  "about.closing.cta": "Explore the fragrances",
  "about.closing.cta_gifts": "See the gift sets",

  // ── About: essentials (facts / vision / mission) ──
  "about.facts.eyebrow": "At a glance",
  "about.facts.1.label": "Founded",
  "about.facts.1.value": "Dubai, United Arab Emirates",
  "about.facts.2.label": "House type",
  "about.facts.2.value": "Independent perfume house",
  "about.facts.3.label": "Specialty",
  "about.facts.3.value": "Fragrances, gift sets & bakhoor",
  "about.facts.4.label": "Markets served",
  "about.facts.4.value": "UAE · Saudi Arabia · Oman · Iraq",
  "about.vision.eyebrow": "Vision",
  "about.vision.title": "An Emirati house read across the world.",
  "about.vision.body":
    "To grow Lecmo from the Emirates into a house whose fragrances are recognised across the Gulf and beyond — without losing the quiet character it started with.",
  "about.mission.eyebrow": "Mission",
  "about.mission.title": "A scent that resembles the person wearing it.",
  "about.mission.body":
    "To compose balanced, lasting fragrances and present them in a way that makes every bottle feel personal — from the first note to the moment the box opens.",

  // ── Journal ──
  "journal.eyebrow": "Journal",
  "journal.title": "Slow words, slow scent.",
  "journal.subtitle": "Notes from the atelier — on oud, rose, and the rituals between.",
  "journal.post1.category": "Notes",
  "journal.post1.minutes": "4 min",
  "journal.post1.title": "The Language of Rose",
  "journal.post1.excerpt":
    "Why a single petal can carry an entire memory — a meditation on the most overused, and most misunderstood, of all notes.",
  "journal.post2.category": "Origin",
  "journal.post2.minutes": "7 min",
  "journal.post2.title": "On Oud and Patience",
  "journal.post2.excerpt":
    "Aging the resin that defines an empire. We follow a single piece of agarwood from forest to flacon.",
  "journal.post3.category": "Ritual",
  "journal.post3.minutes": "3 min",
  "journal.post3.title": "A Drop, A Ritual",
  "journal.post3.excerpt":
    "How to wear a fragrance, slowly. Five small ceremonies to bring back to your morning.",
  "journal.meta.title": "Journal — Lecmo | Fragrance Notes & Stories",
  "journal.meta.description":
    "Essays on fragrance, notes, and rituals from the Lecmo journal — how our perfumes are composed and worn.",

  // ── Contact ──
  "contact.eyebrow": "Contact",
  "contact.title": "A quiet word.",
  "contact.subtitle": "Reach the atelier — by message, mail, or a visit by appointment.",
  "contact.whatsapp_title": "WhatsApp Concierge",
  "contact.email_title": "Email",
  "contact.atelier_title": "Atelier",
  "contact.atelier_line1": "Dubai",
  "contact.atelier_line2": "United Arab Emirates",
  "contact.form.name": "Your name",
  "contact.form.email": "Email",
  "contact.form.message": "Your message",
  "contact.form.submit": "Send message",
  "contact.form.open_email": "Open email",
  "contact.form.email_hint": "This opens a draft in your email app. Review it and press Send there to contact us. Your message stays here until you leave this page.",
  "contact.form.thanks": "Thank you — our concierge will be in touch shortly.",
  "footer.partners": "Business Partnerships",
  "contact.partners.title": "Are you a distributor or retailer?",
  "contact.partners.body": "Discover Lecmo's business partnership program — distribution terms and wholesale pricing.",
  "contact.partners.cta": "Open Partner Portal",
  "contact.meta.title": "Contact Lecmo | Dubai Atelier & Customer Care",
  "contact.meta.description":
    "Reach the Lecmo team for orders, gifting advice, wholesale, or atelier enquiries — WhatsApp, email, or our Dubai atelier.",

  // ── UAE ads landing ──
  "uae.meta.title": "Lecmo UAE | Niche Perfumes Delivered in the Emirates",
  "uae.meta.description":
    "Lecmo perfumes for the UAE. Fast local delivery, cash on delivery, and gift wrapping from our Dubai atelier.",
  "uae.hero.title": "Made in Dubai. Delivered to your door.",
  "uae.hero.subtitle": "Niche fragrances, local delivery, and a gift box worth keeping.",
  "uae.hero.alt": "Lecmo perfume bottle in warm Dubai light",
  "uae.cta.shop": "Shop the collection",
  "uae.cta.whatsapp": "Ask on WhatsApp",
  "uae.perks.delivery.title": "UAE delivery",
  "uae.perks.delivery.body": "Shipped locally across all seven emirates.",
  "uae.perks.cod.title": "Pay your way",
  "uae.perks.cod.body": "Card or cash on delivery at checkout.",
  "uae.perks.gift.title": "Gift ready",
  "uae.perks.gift.body": "Signature box and a personal note, free.",
  "uae.perks.support.title": "Real people",
  "uae.perks.support.body": "Our Dubai team answers on WhatsApp.",
  "uae.closing.title": "Not sure which scent?",
  "uae.closing.body": "Tell us what you love and we will pick two options for you.",


  // ── Gifts (page hero + steps) ──
  "gifts.page.title": "A gift that lingers.",
  "gifts.page.subtitle":
    "Choose a fragrance, write a note, and we'll wrap it in our signature box. Delivered on the day that matters most.",
  "gifts.page.image_alt": "Luxury gift box opening with golden light",
  "gifts.steps.s1.n": "01",
  "gifts.steps.s1.title": "Choose a Fragrance",
  "gifts.steps.s1.desc": "From our curated collection.",
  "gifts.steps.s2.n": "02",
  "gifts.steps.s2.title": "Compose a Note",
  "gifts.steps.s2.desc": "Up to 300 characters, handwritten on linen.",
  "gifts.steps.s3.n": "03",
  "gifts.steps.s3.title": "Schedule the Surprise",
  "gifts.steps.s3.desc": "Pick a delivery date — we'll do the rest.",
  "gifts.meta.title": "Perfume Gift Sets — Lecmo | Cinematic Gifting",
  "gifts.meta.description":
    "Send a Lecmo perfume as a gift: cinematic gift boxes, handwritten notes, and delivery straight to your recipient.",

  // ── Legal: Shipping ──
  "legal.shipping.eyebrow": "Logistics",
  "legal.shipping.title": "Shipping",
  "legal.shipping.intro": "Delivery is free in the United Arab Emirates, Oman and Qatar. Saudi Arabia: SAR 100 for orders up to and including SAR 1,000; free above SAR 1,000. Delivery within Iraq costs USD 10 per order. Orders in these five countries are fulfilled locally from within the destination country.",
  "legal.shipping.service_intro": "We deliver every day, including weekends and public holidays. Delivery times below are estimates, not guarantees.",


  "legal.shipping.h2_supported": "Local delivery",
  "legal.shipping.col_country": "Country",
  "legal.shipping.col_time": "Delivery time",
  "legal.shipping.col_note": "Shipping charge",
  "legal.shipping.same_day": "Usually the same day before noon; next day at or after noon",
  "legal.shipping.same_day_detail": "Orders placed before 12:00 noon in the destination's local time usually arrive the same day. Orders placed at or after noon are expected the next day.",
  "legal.shipping.country_ae": "United Arab Emirates",
  "legal.shipping.country_sa": "Saudi Arabia",
  "legal.shipping.country_om": "Oman",
  "legal.shipping.country_iq": "Iraq",
  "legal.shipping.country_br": "Brazil",
  "legal.shipping.country_us": "United States",

  "legal.shipping.us_orders": "Orders to the United States can be placed directly on our website. Standard shipping costs USD 125 when the eligible order subtotal after discounts and before taxes is below USD 350, and is free at USD 350 or more. Available shipping options and applicable charges are shown at checkout.",
  "header.country_shipping_note": "Shipping is available to the Gulf, Iraq, the United States, Canada, Europe and South America only.",
  "header.country_shipping_link": "See shipping destinations",
  "legal.shipping.us_timing": "Delivery estimates depend on the destination and shipment. Contact our team for an estimate; delivery times are estimates, not guarantees.",
  "legal.shipping.us_charges": "Free shipping covers the shipping charge only. Applicable taxes, customs duties or import charges may be additional.",
  "cart.destination_unavailable": "Checkout is not currently available for this destination. Please contact us on WhatsApp.",
  "legal.shipping.added_title": "Covered international destinations",
  "legal.shipping.added_rates": "For the destinations listed below, Standard shipping costs USD {fee} when the order subtotal after discounts and before taxes is below USD {threshold}. Standard shipping is free at USD {threshold} or more. Shipping options and applicable charges are confirmed at checkout.",
  "legal.shipping.added_timing": "Estimated delivery: {minDays}–{maxDays} business days. This is an estimate, not a carrier guarantee.",
  "legal.shipping.added_countries": "Destinations covered by this Standard shipping policy:",

  "legal.shipping.h2_rest": "Other destinations",
  "legal.shipping.col_region": "Region",
  "legal.shipping.rest_world": "Destinations not listed above",
  "legal.shipping.within_week": "7 days expected",

  "legal.shipping.note": "The noon cutoff uses the destination's local time. Local delivery periods are calendar days, including weekends and public holidays. International Standard shipping estimates use business days.",

  "legal.shipping.meta.title": "Shipping & Delivery — Lecmo",
  "legal.shipping.meta.description": "LECMO shipping information, including direct US checkout and free Standard shipping on eligible US orders of USD 350 or more.",
  "legal.shipping.country_qa": "Qatar",
  "legal.shipping.sa_time": "Greater Riyadh: usually same day before noon, next day at or after noon. Outside Greater Riyadh: 1–3 days.",
  "legal.shipping.iq_time": "Baghdad: usually same day before noon, next day at or after noon. Outside Baghdad: 1–3 days.",
  "legal.shipping.sa_fee": "SAR 100 up to and including SAR 1,000; free above SAR 1,000",
  "legal.shipping.kw_bh_title": "Kuwait and Bahrain",
  "legal.shipping.kw_bh_rates": "Shipping costs AED 100 for orders below AED 1,000, and is free at AED 1,000 or more. Estimated transit time: 1–2 business days. Available options and final charges are confirmed at checkout.",
  "legal.shipping.added_more": "The same rates are also configured for Australia, Egypt, Japan, Morocco, Tunisia and Türkiye. Availability for your destination is confirmed at checkout. New Zealand is not currently available for checkout.",
  "legal.shipping.free": "Free",
  "legal.shipping.iraq_fee": "USD 10 per order",
  "legal.shipping.international_fee": "For destinations not listed above, shipping charges depend on the shipment and destination. Contact our WhatsApp concierge before ordering to confirm the shipping charge before you confirm and pay for the order.",


  // ── Legal: Returns ──
  "legal.returns.eyebrow": "Care",
  "legal.returns.title": "Returns & Damaged Items",
  "legal.returns.intro":
    "For hygiene, safety, and authenticity reasons — and in line with international perfumery practice — fragrances cannot be returned or exchanged once they have left our hands. The only exception is when something has gone wrong on our side: a damaged bottle, a broken seal, or the wrong fragrance in the box. In that case, we'll replace it or refund you in full, no questions asked.",

  "legal.returns.h2_no_returns": "Why we cannot accept returns",
  "legal.returns.p_no_returns":
    "Perfume is an intimate product. Once a bottle leaves our atelier, we cannot guarantee how it has been stored, sprayed, or handled — and we will never put a returned bottle back into another customer's hands. This is the standard policy of every serious house worldwide. By placing your order, you confirm that you understand this.",

  "legal.returns.h2_our_mistake": "When the mistake is ours",
  "legal.returns.p_our_mistake":
    "If the bottle arrives broken, the seal is tampered with, the box contains the wrong fragrance, or the order is materially different from what you bought, please contact us within 48 hours of delivery with a clear photo of the issue and the unopened item. We will collect the order at our cost and either replace it (priority dispatch) or refund the full amount to your original payment method within 7 business days.",

  "legal.returns.h2_how": "How to report an issue",
  "legal.returns.p_how":
    "Message us on WhatsApp or write to info@lecmo.org with your order number and a photo of the issue. We monitor both channels every day. The faster you reach out, the faster we can make it right.",

  "legal.returns.h2_cancel": "Cancelling before dispatch",
  "legal.returns.p_cancel":
    "If you change your mind before the order has been dispatched, contact us immediately and we will cancel it and refund you in full. Once the courier has picked up the package, we can no longer cancel — the order is on its way.",

  "legal.returns.meta.title": "Returns & Exchanges — Lecmo",
  "legal.returns.meta.description":
    "How to return or exchange a Lecmo order: eligibility windows, condition rules, and refund timing.",

  // ── Legal: Cookies ──
  "footer.cookies": "Cookies",
  "legal.cookies.eyebrow": "Transparency",
  "legal.cookies.title": "Cookie Policy",
  "legal.cookies.intro":
    "Cookies are small files stored on your device. We use them to keep your cart and session alive, to remember your market and language, and to understand how the store is used. We never store payment details in a cookie.",
  "legal.cookies.h2_essential": "Essential cookies",
  "legal.cookies.p_essential":
    "These keep you signed in, hold your cart between visits, remember your chosen country, currency, and language, and protect forms against abuse. The store cannot function without them, so they cannot be switched off.",
  "legal.cookies.h2_analytics": "Measurement cookies",
  "legal.cookies.p_analytics":
    "We measure page views, product views, and checkout steps so we can see where the experience breaks. This data is aggregated and is never used to identify you personally.",
  "legal.cookies.h2_ads": "Advertising cookies",
  "legal.cookies.p_ads":
    "When you arrive from an advertisement, our advertising partners may set a cookie so a purchase can be attributed to the campaign that brought you here. You can opt out at any time in your browser or in your account privacy settings on those platforms.",
  "legal.cookies.h2_control": "Your control",
  "legal.cookies.p_control":
    "You can delete or block cookies at any time from your browser settings. Blocking essential cookies will break sign-in and the cart. To request deletion of data linked to your account, write to info@lecmo.org.",
  "cookie_consent.eyebrow": "Cookies",
  "cookie_consent.body": "We use cookies to keep your cart, language, and sign-in working. With your consent, we also use analytics and marketing cookies to improve the store and measure our campaigns.",
  "cookie_consent.policy_link": "Cookie Policy",
  "cookie_consent.accept": "Agree",
  "cookie_consent.reject": "Disagree",
  "legal.cookies.meta.title": "Cookie Policy — Lecmo",
  "legal.cookies.meta.description":
    "How Lecmo uses cookies for analytics, preferences, and advertising — and how to control them.",

  // ── Legal: Copyright & Intellectual Property ──
  "footer.copyright_page": "Copyright",
  "footer.copyright_line": "© {year} Lecmo. All rights reserved.",
  "footer.trn": "TRN: 105053561400003",
  "legal.copyright.eyebrow": "Ownership",
  "legal.copyright.title": "Copyright & Intellectual Property",
  "legal.copyright.intro":
    "Everything on this site — the Lecmo name and marks, the fragrance names and descriptions, photography, film, layout, and code — is owned by Lecmo (TRN 105053561400003) or licensed to it, and is protected by copyright and trademark law.",
  "legal.copyright.h2_owned": "What is protected",
  "legal.copyright.p_owned":
    "This includes our logo and wordmark, product and campaign photography and video, written copy in every language on this site, the visual design of these pages, and the software that runs the store.",
  "legal.copyright.h2_ambassadors": "Licence granted to ambassadors",
  "legal.copyright.p_ambassadors":
    "Approved ambassadors receive a limited, revocable, non-transferable licence to publish Lecmo media supplied inside their account, for the sole purpose of promoting Lecmo with their own referral link. The licence does not allow resale, editing that misrepresents the product, use inside another brand's material, or paid advertising without written permission. It ends automatically when the ambassadorship ends.",
  "legal.copyright.h2_forbidden": "What is not allowed",
  "legal.copyright.p_forbidden":
    "Copying this site or substantial parts of it, cloning its design or code, scraping it automatically, reusing our photography or text on another store or marketplace, or presenting our products as your own brand. Short quotations with a visible credit and a link back are welcome.",
  "legal.copyright.h2_report": "Reporting an infringement",
  "legal.copyright.p_report":
    "If you believe your work has been used here without permission, or you have found a copy of our content elsewhere, write to info@lecmo.org with the exact links and a description. We review every report and act on confirmed cases.",
  "legal.copyright.meta.title": "Copyright & IP — Lecmo",
  "legal.copyright.meta.description":
    "Copyright and intellectual property terms for Lecmo brand assets, imagery, and content.",

  // ── Legal: Accessibility ──
  "footer.accessibility": "Accessibility",
  "legal.accessibility.eyebrow": "Everyone",
  "legal.accessibility.title": "Accessibility Statement",
  "legal.accessibility.intro":
    "Lecmo is built to be usable by everyone, including people who browse with a screen reader, a keyboard only, or with reduced motion and larger text. We aim to meet WCAG 2.1 level AA.",
  "legal.accessibility.h2_features": "What we already do",
  "legal.accessibility.p_features":
    "Descriptive alternative text on product and campaign imagery; labelled buttons and form fields; visible focus outlines for keyboard navigation; full keyboard access to menus, carousels, and the cart; respect for the system setting that reduces motion; contrast-checked colours; and correct language and reading direction for Arabic and English.",
  "legal.accessibility.h2_limits": "Known limitations",
  "legal.accessibility.p_limits":
    "Some third-party screens, such as the payment checkout hosted by our commerce provider, are outside our direct control. Video content does not yet carry captions on every clip. We are working on both.",
  "legal.accessibility.h2_help": "Need help ordering?",
  "legal.accessibility.p_help":
    "If any part of the site blocks you, message us on WhatsApp or write to info@lecmo.org and we will complete the order with you personally, read the product details aloud, or send them in the format you prefer.",
  "legal.accessibility.h2_feedback": "Report a barrier",
  "legal.accessibility.p_feedback":
    "Email info@lecmo.org with the page link and what happened. We answer accessibility reports first and aim to reply within two working days.",
  "legal.accessibility.meta.title": "Accessibility Statement — Lecmo",
  "legal.accessibility.meta.description":
    "Our commitment to making lecmo.com usable for everyone, plus how to report an accessibility issue.",


  // ── Legal: Privacy ──
  "legal.privacy.eyebrow": "Trust",
  "legal.privacy.title": "Privacy Policy",
  "legal.privacy.intro":
    "We collect only what we need to deliver your order, recognize you on your next visit, and improve the experience. We never sell your data, and we never share it for purposes beyond running this store. Every transaction is encrypted end-to-end.",

  "legal.privacy.h2_collect": "What we collect",
  "legal.privacy.p_collect":
    "Your name, shipping address, phone number, email, and the fragrances you buy or save to your wishlist. If you create an account, we also store your password as a one-way hash (we cannot read it) and your language and country preferences. We do not collect payment card details — those go directly to our payment processor and never touch our servers.",

  "legal.privacy.h2_processors": "Who helps us run the store",
  "legal.privacy.p_processors":
    "We rely on a small number of trusted partners: Shopify (payments and order processing), Lovable Cloud / Supabase (account, wishlist, and customer database), Resend (transactional emails), Twilio (WhatsApp notifications), and the Meta, TikTok, Snap, and Google advertising platforms (anonymous behavior signals only — never your name or address). Each one is contractually bound to use your data only on our behalf.",

  "legal.privacy.h2_cookies": "Cookies and tracking",
  "legal.privacy.p_cookies":
    "We use the minimum cookies needed: cart, language, country, and login session. We also use marketing pixels (Meta, TikTok, Snap, Google) to measure which campaigns work — these signals are aggregated and do not identify you personally. You can block them through your browser settings without breaking the site.",

  "legal.privacy.h2_rights": "Your rights",
  "legal.privacy.p_rights":
    "You can ask us at any time to (a) show you all the data we hold about you, (b) correct anything that's wrong, or (c) delete your account and everything tied to it. Write to privacy@lecmo.com from the email address on your account and we will respond within 14 days. Orders that have been shipped must remain in our records for accounting and warranty purposes.",

  "legal.privacy.h2_whatsapp": "WhatsApp & email messaging",
  "legal.privacy.p_whatsapp":
    "We send two kinds of messages over WhatsApp and email. (1) Service messages — order confirmation, shipping updates, delivery, and replies to questions you sent us. These are required to fulfill your order and you receive them as long as you have an open order with us. (2) Marketing messages — wishlist reminders, occasion offers, seasonal launches, and loyalty news. We only send these after you tick the explicit opt-in box at checkout or in your account, and you can stop them at any time by (a) replying STOP to any WhatsApp message, (b) tapping the unsubscribe link at the bottom of any marketing message, or (c) visiting lecmo.com/unsubscribe. Unsubscribing from marketing never affects service messages about your orders. Your WhatsApp number is shared only with Twilio (our messaging provider) and is never sold or rented to third parties.",

  "legal.privacy.h2_retention": "How long we keep things",
  "legal.privacy.p_retention":
    "Account data: as long as your account is active, plus 12 months. Order records: 7 years (legal requirement for accounting). Marketing analytics: aggregated and anonymized after 24 months.",

  "legal.privacy.meta.title": "Privacy Policy — Lecmo",
  "legal.privacy.meta.description":
    "What personal data Lecmo collects, how it is used and stored, and the rights you have over it.",

  // ── Legal: Terms ──
  "legal.terms.eyebrow": "Conditions",
  "legal.terms.title": "Terms of Sale",
  "legal.terms.intro":
    "By placing an order with Lecmo (TRN 105053561400003) you confirm the following, and you agree that these terms are governed by the laws of the United Arab Emirates.",
  "legal.terms.li_intro":
    "You are at least 18 years old.|The address and contact details you provide are accurate.|You have read our shipping, returns, and privacy policies.",

  "legal.terms.h2_pricing": "Prices and currency",
  "legal.terms.li_pricing":
    "Prices are set manually for each market and shown in its local currency (AED, SAR, OMR, USD).|All prices include VAT where applicable.|The amount shown at checkout is the amount charged — no hidden fees.|No surprise customs duties: we ship locally inside each market.",

  "legal.terms.h2_payment": "Payment",
  "legal.terms.li_payment":
    "Major credit and debit cards through our payment processor.|Tabby and Tamara — pay in instalments where available.|Cash on delivery in the markets where it is offered.|Gift orders are card-only — the recipient is never asked to pay.|If a payment is declined the order is not confirmed; try again or contact us.",

  "legal.terms.h2_authenticity": "Authenticity guarantee",
  "legal.terms.li_authenticity":
    "Every fragrance is original, sourced from the maison or its authorized distributors.|Never grey-market, never a replica.|Each bottle is sealed and inspected before dispatch.|Doubt a bottle you received? Send us a photo and we verify it personally.",

  "legal.terms.h2_loyalty": "Loyalty, ambassador, and gift programs",
  "legal.terms.li_loyalty":
    "Points, ambassador commissions, and gift credit are personal and non-transferable.|They hold no cash value outside our store.|Program rules may change from time to time, with notice on this page.|Existing balances are always honored under the rules in force when they were earned.|Any content created by a Lecmo ambassador about Lecmo — including photos, videos, and written posts — may be used by Lecmo for commercial purposes under the ambassador agreement.",

  "legal.terms.h2_ip": "Intellectual property",
  "legal.terms.li_ip":
    "All imagery, copy, brand marks, and the Lecmo name are ours or used under license.|Commercial republication requires written permission.|Sharing on social media with credit is always welcome.",

  "legal.terms.h2_disputes": "Disputes",
  "legal.terms.li_disputes":
    "Write to us first — we resolve nearly every issue within 48 hours.|Unresolved disputes are governed by the laws of the United Arab Emirates.|Exclusive jurisdiction belongs to the courts of Dubai.",

  "legal.terms.h2_changes": "Changes to these terms",
  "legal.terms.li_changes":
    "These terms may be updated occasionally.|The version in force is the one published on this page on the day of your order.|Material changes are highlighted in our newsletter.",


  "legal.terms.meta.title": "Terms of Sale — Lecmo",
  "legal.terms.meta.description":
    "The terms that apply to purchases from Lecmo: pricing, payment, delivery, and cancellation.",

  // ── Gift with Purchase (free gift — neutral, any size/type) ──
  "gift_sample.choose": "Choose your free gift",
  "gift_sample.added": "Added — your free gift",
  "gift_sample.remove": "Remove gift",
  "gift_sample.label": "Free gift",
  "gift_sample.one_per_order": "One free gift per order",
  "gift_sample.changed": "Selection updated",
  "gift_sample.removed_low_cart": "Free gift removed — cart below the minimum",
  "gift_sample.removed_title": "Gift removed",
  "gift_sample.removed_reason_min_cart": "Your cart dropped below {amount}, the minimum to qualify.",
  "gift_sample.removed_reason_min_items": "Your cart now has fewer than {n} items.",
  "gift_sample.removed_reason_market": "The campaign isn't available in your selected market.",
  "gift_sample.removed_reason_unavailable": "The selected sample is no longer available.",
  "gift_sample.removed_reason_generic": "You no longer meet the campaign conditions.",

  // ── Admin: Shopify audit (source of truth) ──
  // ── Admin: tracking consistency readiness ──
  "tracking.status.ok": "Consistent",
  "tracking.status.warn": "Minor gap",
  "tracking.status.danger": "Large gap",
  "tracking.status.insufficient": "Insufficient data",

  // ── Gift accounting ledger ──
  // ── Admin: users / addresses ──
  // ── Admin: gift programs dashboard ──
  "gift_sample.picked_count": "Selected {n} of {max}",
  "gift_sample.picked_max": "You picked all your gifts.",
  // ── سبب الاستحقاق (يظهر داخل بطاقة الهديّة) ──
  "gift_sample.why.congrats": "Congratulations — this gift is yours",
  "gift_sample.why.first_order": "because it's your first order with us.",
  "gift_sample.why.returning": "as a thank-you for coming back.",
  "gift_sample.why.min_cart": "because your order exceeds {amount}.",
  "gift_sample.why.min_items": "because your bag has {n} items or more.",
  "gift_sample.why.market": "as a welcome gift in your region.",
  "gift_sample.why.every_order": "with every order.",
  // ── سطر العيّنة داخل قائمة المشتريات + توست الإضافة ──
  "gift_sample.line_badge": "Free gift from Lecmo",
  "gift_sample.line_free": "Free",
  "gift_sample.line_price_free": "FREE",
  "gift_sample.line_our_gift": "Our gift to you",
  "gift_sample.line_keep_or_remove": "Keep it or remove it — it won't be charged.",
  "gift_sample.added_to_cart": "Your gift was added to your bag ✨",
  "cart.detecting_country": "Detecting your country…",
  "cart.row_no_shipping": "International delivery is arranged through WhatsApp. Contact us to confirm the shipping charge before you confirm and pay for your order. Expected delivery: 7 days.",
  "cart.add": "Add",
  "cart.added_toast": "Added to your bag",
  "cart.add_failed": "Couldn't add to your bag. Please try again.",
  "cart.already_in_cart_toast": "You already added this to your bag",
  "cart.open_action": "View bag",
  "cart.restore_title": "We found a saved bag in your account",
  "cart.restore_body": "Your account has a bag with {n} item(s) from another device. Restore it, or keep the bag on this device?",
  "cart.restore_confirm": "Restore saved bag",
  "cart.restore_keep_current": "Keep current bag",
  "cart.restore_working": "Restoring…",

  "shop.spray_label": "wow",
  "shop.no_image": "No image",
  "shop.title": "The Collection",
  "shop.subtitle": "Discover fragrances crafted for every moment",
  "launch.cta": "Discover {name}",
  "launch.cta_hint": "See details, choose your size and add to bag.",
  "product.zoom_image": "Zoom image",

  "product.add_to_wishlist": "Add to wishlist",
  "product.wishlist_love_note": "We love you too",
  "product.added_to_cart": "Added to bag",

  "product.ar.view_in_space": "View in your space",
  "product.ar.place_in_room": "Place in your room",
  "product.ar.close": "Close 3D view",
  "gift_sample.swapped": "Gift swapped — enjoy your new pick ✨",
  "gift_sample.tap_to_swap": "Tap any sample to swap your gift",
  "gift_sample.added_status": "Gift added to your bag",
  "gift_sample.added_hint_swap": "Tap any other sample to swap it.",
  "gift_sample.added_hint_more": "Pick more samples below if you'd like.",
  // ── Simple inline gift list (Sephora-style) ──
  "gift_sample.unlock_title": "Unlock Your Gift",
  "gift_sample.unlock_all_done": "You've unlocked all gifts!",
  "gift_sample.unlock_progress": "Add {amount} more to unlock your gift",
  "gift_sample.free_prefix": "FREE",
  "gift_sample.pick_one_of": "Pick 1 sample from {n} available",
  "gift_sample.change_title": "Want a different gift?",
  "gift_sample.change_hint": "Remove the current one above, then pick another below.",
  "gift_progress.almost_there": "Almost there!",
  "gift_progress.add_more": "Add {amount} more to unlock {gift}",
  "gift_progress.add_more_generic": "Add {amount} more to get a free gift 🎁",
  "gift_progress.add_more_to_target": "Add {amount} to reach {target} and get a free gift 🎁",
  "gift_progress.one_step_left": "Just one step away! 🎁",
  "gift_progress.continue_shopping": "Continue shopping",
  "gift_progress.your_gift": "Your gift:",
  "gift_progress.choose_from": "Choose from:",
  "gift_progress.plus_more": "+{count} more",
  "gift_sample.add_aria": "Add free gift",
  "gift_sample.add_this_gift": "Add this gift",
  "gift_sample.read_about_product": "Read about this product",
  "gift_sample.locked_unlock_at": "Unlocks at {target} — add {amount} more",
  "gift_sample.remove_aria": "Remove free gift",
  // ── Account (shared) ──
  "common.loading": "Loading…",
  "account.card.orders": "My orders",
  "account.card.recipients": "Address book",
  "account.card.profile": "My address",
  "account.wishlist.remove_aria": "Remove from wishlist",

  // ── Account › Orders ──
  "account.orders.title": "My orders",
  "account.orders.subtitle": "Order history",
  "account.orders.empty": "No orders yet",
  "account.orders.empty_sub": "When you place your first order it will appear here with shipment tracking.",
  "account.orders.shop_cta": "Discover fragrances",
  "account.orders.error": "We couldn't load your orders. Please try again later.",
  "account.orders.placed_on": "Placed on {date}",
  "account.orders.items_count": "{count} item(s)",
  "account.orders.track_shipment": "Track shipment",
  "account.orders.no_tracking": "Tracking will appear once the order ships.",
  "thankyou.cta_whatsapp": "Chat on WhatsApp",
  "account.orders.fulfillment.fulfilled": "Delivered",
  "account.orders.fulfillment.partial": "Partially shipped",
  "account.orders.fulfillment.shipped": "Shipped",
  "account.orders.fulfillment.unfulfilled": "Preparing",
  "account.orders.financial.paid": "Paid",
  "account.orders.financial.pending": "Pending",
  "account.orders.financial.refunded": "Refunded",
  "account.orders.financial.partially_refunded": "Partially refunded",
  "account.orders.financial.voided": "Voided",
  "account.orders.financial.authorized": "Authorized",

  // ── Account › Recipients ──
  "account.recipients.title": "Address book",
  "account.recipients.subtitle": "Saved recipients you've sent gifts to.",
  "account.recipients.empty": "No saved recipients yet",
  "account.recipients.empty_sub": "When you send a gift, the recipient will be saved here for next time.",
  "account.recipients.delete": "Delete",
  "account.recipients.delete_confirm": "Delete this recipient permanently?",
  "account.recipients.deleted": "Recipient deleted",

  // ── Account › Profile ──
  "account.profile.title": "Personal address",
  "account.profile.subtitle": "Used when sending gifts to yourself or as your default shipping address.",
  "account.profile.display_name": "Display name",
  "account.profile.phone": "Phone number",
  "account.profile.country": "Country",
  "account.profile.city": "City",
  "account.profile.address": "Street address",
  "account.profile.driver_notes": "Notes for the courier (optional)",
  "account.profile.save": "Save changes",
  "account.profile.saved": "Profile updated",

  // ── Account › Meta & confirmations ──
  "account.meta.title": "My Account — Lecmo",
  "account.meta.description": "Manage your Lecmo account: orders, addresses, wishlist, and rewards in one place.",
  "account.wishlist.meta.title": "My Wishlist — Lecmo",
  "account.wishlist.meta.description": "Your saved Lecmo fragrances and gift sets, ready when you are.",
  "account.orders.meta.title": "My Orders — Lecmo",
  "account.orders.meta.description": "Track your Lecmo orders and follow each delivery from atelier to door.",
  "account.recipients.meta.title": "Address Book — Lecmo",
  "account.recipients.meta.description": "Saved addresses and gift recipients for faster Lecmo checkout.",
  "account.profile.meta.title": "Personal Address — Lecmo",
  "account.profile.meta.description": "Update your personal delivery address and contact details for Lecmo orders.",
  "account.section.profile": "Profile",
  "account.section.shopping": "Shopping",
  "account.section.ambassador": "Ambassador program",
  "account.section.support": "Support",
  "account.row.profile": "Personal information",
  "account.row.orders": "My orders",
  "account.row.wishlist": "Wishlist",
  "account.row.recipients": "Saved recipients",
  "account.row.analytics": "My analytics",
  "account.row.media": "Photo & video sharing library",
  "account.row.admin_dashboard": "Admin dashboard",
  "account.row.country": "Country & currency",
  "account.row.language": "Language",
  "account.row.notifications": "Notifications",
  "account.row.whatsapp": "WhatsApp us",
  "account.row.help": "Help center",

  // ── Account › Info & policies ──
  "account.section.info": "Information & policies",
  "account.row.track_order": "Track an order",
  "account.row.shipping": "Shipping",
  "account.row.returns": "Returns & exchanges",
  "account.row.faq": "FAQ",
  "account.row.terms": "Terms & conditions",
  "account.row.privacy": "Privacy policy",
  "account.row.ledger": "Wallet history",
  "account.row.coupons": "Balance withdrawal cards",
  "account.section.wallet": "Wallet & earnings",
  "account.row.gifts": "My gifts",
  "account.row.ambassador": "Ambassador program",
  "account.section.loyalty": "Loyalty",
  "account.row.loyalty": "My loyalty points",
  "account.row.loyalty_hint": "Your points and wallet",
  "ambassadors.paused.title": "The ambassador program is currently unavailable",
  "ambassadors.paused.body": "Your account, points and balances are safe. Thank you for being with Lecmo Perfume.",
  "ambassadors.paused.cta": "Go to my account",
  "upjoin.title": "Join as a Lecmo Perfume Ambassador",
  "upjoin.subtitle": "Fill in your details. We review every request personally.",
  "upjoin.f.first_name": "First name",
  "upjoin.f.last_name": "Last name",
  "upjoin.f.email": "Email",
  "upjoin.f.country": "Country",
  "upjoin.f.phone": "Phone (optional)",
  "upjoin.f.instagram": "Instagram",
  "upjoin.f.tiktok": "TikTok (optional)",
  "upjoin.f.youtube": "YouTube (optional)",
  "upjoin.f.terms_prefix": "I agree to the",
  "upjoin.f.terms_link": "ambassador terms",
  "upjoin.submit": "Send request",
  "upjoin.sending": "Sending…",
  "upjoin.login": "Sign in to your ambassador account",
  "upjoin.err.invalid": "Please check the required fields and accept the terms.",
  "upjoin.err.exists": "You already have an ambassador account. Please sign in.",
  "upjoin.err.rate_limited": "Too many attempts. Please try again later.",
  "upjoin.err.unavailable": "Registration is temporarily unavailable. Please try again later.",
  "upjoin.success.meta": "Request received — Lecmo Perfume",
  "upjoin.success.title": "Your request has been received",
  "upjoin.success.body": "You will receive a message in your email.",
  "upjoin.success.cta": "Back to home",
  "upjoin.terms.title": "Ambassador Terms",
  "upjoin.terms.p1": "By joining, you agree to promote Lecmo Perfume honestly and in line with each platform's rules.",
  "upjoin.terms.p2": "Your account is managed through our partner platform UpPromote, which handles links, tracking and payouts.",
  "upjoin.terms.p3": "Every request is reviewed. Lecmo Perfume may accept, decline or end participation at any time.",
  "upjoin.terms.p4": "These terms may be updated. The current version is always on this page.",
  "account.row.application": "Upgrade application",
  "account.applications.meta_title": "Upgrade application — Lecmo",
  "account.ledger.meta_title": "Wallet history — Lecmo",
  "account.coupons.meta_title": "Balance withdrawal cards — Lecmo",

  // ── Ambassadors: landing page ──
  "ambassadors.pageTitle": "Lecmo Ambassador Program",
  "ambassadors.hero.eyebrow": "Lecmo Ambassadors",
  "ambassadors.hero.title": "Earn from your love of perfume.",
  "ambassadors.hero.subtitle":
    "Share what moves you. Get rewarded — in cash, or in Lecmo creations made for the few.",
  "ambassadors.hero.cta_apply": "Become an Ambassador",
  "ambassadors.hero.cta_dashboard": "Open my dashboard",
  "ambassadors.hero.cta_status": "View my application",

  "ambassadors.tiers.title": "Three quiet tiers",
  "ambassadors.tiers.subtitle": "You grow as your audience grows.",
  "ambassadors.tiers.next_step_label": "How to advance",
  "ambassadors.tiers.intro.title": "Grow your place, one share at a time",
  "ambassadors.tiers.intro.subtitle": "Three tiers. Clear steps. No hidden rules.",
  "ambassadors.tiers.decouverte.name": "Lecmo Lover",
  "ambassadors.tiers.decouverte.desc": "Your first step. Share your link and earn on every sale.",
  "ambassadors.tiers.decouverte.next": "Make your first sales to unlock Ambassador status.",
  "ambassadors.tiers.active.name": "Lecmo Ambassador",
  "ambassadors.tiers.active.desc": "Higher commissions and early access to new compositions.",
  "ambassadors.tiers.active.next": "Keep sharing, hit the monthly threshold, and apply for Super Star.",
  "ambassadors.tiers.elite.name": "Super Star Ambassador",
  "ambassadors.tiers.elite.desc": "For voices we trust. Curated gifts and personal credit.",
  "ambassadors.tiers.elite.next": "Stay consistent. Super Stars are invited to exclusive campaigns.",

  "ambassadors.circle.title": "Life of a Lecmo Ambassador",
  "ambassadors.circle.subtitle": "the quiet loop behind every share",
  "ambassadors.circle.signup": "Open your account",
  "ambassadors.circle.link": "Get your own link",
  "ambassadors.circle.content": "Create your content",
  "ambassadors.circle.share": "Share with your circle",
  "ambassadors.circle.track": "Track clicks & sales",
  "ambassadors.circle.reward": "Earnings & gifts",
  "ambassadors.circle.explainer":
    "Lecmo’s loyalty program turns every share into a reward. From your first tracked click you earn as a Lecmo Lover, then grow through Ambassador and Super Star tiers with higher commissions, early access to new creations, surprise gifts, and cashback or credit options.",

  "ambassadors.uppromote.title": "Join by direct sign-up or through UpPromote",
  "ambassadors.uppromote.body":
    "You can apply to the Lecmo Ambassador Program through direct sign-up on our site. If you discovered us through UpPromote, your application will be forwarded to us automatically. UpPromote is a sign-up and tracking channel used by Lecmo; it is not a party to the ambassador agreement and does not handle payouts. Your commissions, tier progress, and withdrawals are managed by Lecmo under the terms you accept.",

  "ambassadors.uppromote.termsCta": "Read the ambassador terms",


  "ambassadors.how.title": "How it works",
  "ambassadors.how.step1.title": "Apply",
  "ambassadors.how.step1.desc": "Tell us where you share — Instagram, TikTok, or your circle.",
  "ambassadors.how.step2.title": "Get your link",
  "ambassadors.how.step2.desc": "One personal link. Yours forever.",
  "ambassadors.how.step3.title": "Share",
  "ambassadors.how.step3.desc": "On bio, stories, videos, or whispered to friends.",
  "ambassadors.how.step4.title": "Earn",
  "ambassadors.how.step4.desc": "Watch your dashboard fill — then redeem: An instant store card usable at checkout or cashable for its value, and loyalty points redeemed into a store coupon. The payout channels available to you — including bank transfer — are shown in your ambassador dashboard.",

  "ambassadors.stages.title": "Stages & Benefits",
  "ambassadors.stages.subtitle": "How the ambassador system works, step by step.",
  "ambassadors.stages.advance.title": "What is next",
  "ambassadors.stages.signup.title": "Open your account",
  "ambassadors.stages.signup.benefit": "Simply create a customer account on the official site. You instantly become a Lecmo Lover, the first stage of the ambassador program.",
  "ambassadors.stages.signup.advance": "You get your own link and start your journey in the program.",
  "ambassadors.stages.link.title": "Get your own link",
  "ambassadors.stages.link.benefit": "One short link, made just for you, works across all supported markets.",
  "ambassadors.stages.link.advance": "You create your content and start working.",
  "ambassadors.stages.content.title": "Create your content and start working",
  "ambassadors.stages.content.benefit": "Use ready captions and your own media library to pick whatever suits you to promote.",
  "ambassadors.stages.content.advance": "You start earning from your activity, and the more creative you are, the more beautiful and profitable the results.",
  "ambassadors.stages.share.title": "Share with your circle",
  "ambassadors.stages.share.benefit": "Post an Instagram feed post or story, Snapchat, TikTok, YouTube, or share directly on WhatsApp with your link attached, it is usually pasted automatically.",
  "ambassadors.stages.share.advance": "Tap share on any media item, copy the caption or add your own text, and publish on your favorite platform, or create your own content.",

  "ambassadors.stages.track.title": "Track clicks & sales",
  "ambassadors.stages.track.benefit": "Watch clicks, orders, and rewards in real-time via a dedicated platform that tracks your account activity in detail.",
  "ambassadors.stages.track.advanceLabel": "How to move to the next stage",
  "ambassadors.stages.track.advance": "There are 3 ambassador tiers. You move up automatically based on your activity, and your rewards and earnings rise with each level.",
  "ambassadors.stages.reward.title": "Earnings & gifts",
  "ambassadors.stages.reward.benefit": "Redeem your balance: An instant store card usable at checkout or cashable for its value, and loyalty points redeemed into a store coupon. The payout channels available to you — including bank transfer — are shown in your ambassador dashboard. Plus surprise gifts.",
  "ambassadors.stages.reward.advanceLabel": " ",
  "ambassadors.stages.reward.advance": "It is the most rewarding ambassador program out there.",


  "ambassadors.faq.title": "Questions",
  "ambassadors.faq.q1.q": "Is it free to join?",
  "ambassadors.faq.q1.a": "Yes. We review each application by hand to keep the circle small.",
  "ambassadors.faq.q2.q": "How am I paid?",
  "ambassadors.faq.q2.a": "An instant store card usable at checkout or cashable for its value, and loyalty points redeemed into a store coupon. The payout channels available to you — including bank transfer — are shown in your ambassador dashboard. Bank transfers go to an IBAN in your own name once you reach the minimum shown in your dashboard.",
  "ambassadors.faq.q3.q": "Do I need a large following?",
  "ambassadors.faq.q3.a": "No. We value taste over numbers. A small, engaged circle works beautifully.",
  "ambassadors.faq.q4.q": "Can I lose my spot?",
  "ambassadors.faq.q4.a": "Only if you misrepresent the brand. Your earnings remain yours.",
  "ambassadors.faq.q5.q": "Is this a multi-level program?",
  "ambassadors.faq.q5.a":
    "Two levels only — you and ambassadors you personally recruit. By design, and by law.",

  "ambassadors.meta.title": "Lecmo Ambassadors | Share Perfume, Earn Rewards",
  "ambassadors.meta.description":
    "Join the Lecmo ambassador programme: share your link, track your audience, and earn rewards.",

  // ── Ambassadors: apply form ──
  "ambassadors.apply.title": "Apply to join",
  "ambassadors.apply.subtitle": "Five quiet questions. We answer within a few days.",
  "ambassadors.apply.field.display_name": "Your name as it appears in the program",
  "ambassadors.apply.field.bio": "Tell us briefly about you",
  "ambassadors.apply.field.bio_help": "280 characters maximum.",
  "ambassadors.apply.field.instagram": "Instagram (optional)",
  "ambassadors.apply.field.tiktok": "TikTok (optional)",
  "ambassadors.apply.field.note": "Why do you want to share Lecmo?",
  "ambassadors.apply.field.note_help": "A few honest sentences are enough.",
  "ambassadors.apply.terms_agree": "I have read and agree to the {terms_link}.",
  "ambassadors.apply.terms_link": "program terms",
  "ambassadors.apply.cta": "Send my application",
  "ambassadors.apply.sending": "Sending…",
  "ambassadors.apply.success.title": "Application received",
  "ambassadors.apply.success.body":
    "Thank you. We review every application by hand and will reach out soon.",
  "ambassadors.apply.success.cta": "Back to home",
  "ambassadors.apply.pending.title": "Your application is under review",
  "ambassadors.apply.pending.body":
    "We are reading carefully. Most decisions take a few days.",
  "ambassadors.apply.rejected.title": "Application not accepted this time",
  "ambassadors.apply.rejected.body":
    "Your previous application was not accepted. You may apply again with new context.",
  "ambassadors.apply.error.display_name_required": "Your name is required.",
  "ambassadors.apply.error.bio_too_long": "Please keep your bio under 280 characters.",
  "ambassadors.apply.error.handle_invalid": "This handle does not look right.",
  "ambassadors.apply.error.note_required": "Please share a short note.",
  "ambassadors.apply.error.terms_required": "Please accept the program terms.",
  "ambassadors.apply.error.already_exists": "You already have an application on file.",
  "ambassadors.apply.error.generic": "Something went wrong. Please try again.",
  "ambassadors.apply.login_required":
    "Please sign in to apply — your account anchors your earnings.",

  // ── Ambassadors: terms page ──
  "ambassadors.terms.legacy_title": "Program terms",
  "ambassadors.terms.intro":
    "These terms govern your participation in Lecmo Ambassadors. Plain language, no surprises.",
  "ambassadors.terms.h1": "Who can join",
  "ambassadors.terms.p1":
    "Anyone with a registered Lecmo account, aged 18 or older, who can lawfully receive payment in their country.",
  "ambassadors.terms.h2": "How earnings work",
  "ambassadors.terms.p2":
    "You earn a reward each time someone purchases through your personal link. Your reward is held for a review period (in case of refunds), then becomes available for withdrawal. The exact hold period and values appear in your own incentives file inside your ambassador dashboard.",
  "ambassadors.terms.h3": "How you are paid",
  "ambassadors.terms.p3":
    "An instant store card usable at checkout or cashable for its value, and loyalty points redeemed into a store coupon. The payout channels available to you — including bank transfer — are shown in your ambassador dashboard. Bank transfers go to an IBAN in your own name and a minimum withdrawal threshold shown in your dashboard may apply.",
  "ambassadors.terms.h4": "What is not allowed",
  "ambassadors.terms.p4":
    "Spam, deceptive claims, paid traffic to your link without our written consent, or rewarding yourself by buying through your own link. Any of these may suspend your account and forfeit pending earnings.",
  "ambassadors.terms.h5": "Network levels",
  "ambassadors.terms.p5":
    "You may earn on ambassadors you personally recruit (one level only). You will never earn on ambassadors recruited by them. This is by design and by law.",
  "ambassadors.terms.h6": "Changes",
  "ambassadors.terms.p6":
    "We may adjust reward rates over time. Any change applies only to events after the change — your historical earnings are never recalculated.",
  "account.ambassador.title": "My Ambassador dashboard",

  "ambassador.scenarios.title": "How you earn",
  "ambassador.scenarios.intro": "Ways to earn with Lecmo. The reward type and values depend on your current tier and are set by the Lecmo team.",
  "ambassador.scenarios.share.title": "You share your link",
  "ambassador.scenarios.share.body": "Every genuine click on your link is tracked and rewarded, even without a purchase.",
  "ambassador.scenarios.purchase.title": "A friend buys",
  "ambassador.scenarios.purchase.body": "Each order through your link adds a reward to your wallet as pending, and becomes available once the return window has passed.",
  "ambassador.scenarios.recruit.title": "A friend becomes an active ambassador",
  "ambassador.scenarios.recruit.body": "When someone you recruited becomes active, you get a one-time reward — stackable, no cap.",
  "ambassador.scenarios.recruit.gen2": "Plus an ongoing share on every sale they make afterwards.",
  "ambassador.scenarios.kind.cash": "Reward type on your tier: wallet cash balance.",
  "ambassador.scenarios.kind.percent": "Reward type on your tier: a percentage of the order value, added as wallet cash.",
  "ambassador.scenarios.kind.points": "Reward type on your tier: loyalty points.",
  "ambassador.scenarios.redeem_base": "How you cash out: your wallet balance is redeemed as an instant store card usable at checkout or cashable for its value, and loyalty points are redeemed into a store coupon.",
  "ambassador.scenarios.redeem_bank": "A bank transfer to an IBAN in your own name is also available once your available balance reaches the minimum of {min}.",
  "ambassador.scenarios.monthly_update_note": "Exact amounts, reward types and thresholds depend on your tier and may be updated by the Lecmo team — check your dashboard for current values.",

  "ambassador.scenarios.signature_partner.title": "Signature Partner — separate agreement",
  "ambassador.scenarios.signature_partner.body": "Some ambassadors sign a direct contract with Lecmo (monthly fee + custom terms) outside this public program. The rules above do not apply to them.",

  "account.ambassador.subtitle": "Your link, your earnings, your network.",
  "account.ambassador.code_label": "Your ambassador code",

  "account.ambassador.balance.title": "Wallet",
  "account.ambassador.balance.available": "Available",
  "account.ambassador.balance.pending": "Pending (under hold)",
  "account.ambassador.balance.paid": "Paid out",
  "account.ambassador.balance.empty": "No earnings yet — share your link to begin.",


  "account.ambassador.stats.title": "Last 30 days",
  "account.ambassador.stats.clicks": "Clicks",
  "account.ambassador.stats.sales": "Sales",

  "account.ambassador.channels.title": "Where your sales come from",
  "account.ambassador.channels.subtitle": "Last 30 days — auto-detected from where visitors arrive.",
  "account.ambassador.channels.col_clicks": "Clicks",
  "account.ambassador.channels.col_sales": "Sales",
  "account.ambassador.channels.col_earnings": "Earnings",
  "account.ambassador.channels.empty": "No clicks yet — share your link to see analytics here.",
  "account.ambassador.channels.tip": "Your highest earnings come from {channel} — focus there.",

  "account.ambassador.products.title": "Top performing products",

  // ── Admin: ambassadors panel ──
  // ── Admin: UpPromote import (entry channel only) ──
  // ── Ambassadors: footer banner ──
  "ambassadors.banner.text": "Join Lecmo Ambassadors — earn from your love of perfume.",
  "ambassadors.banner.cta": "Learn more",
  "ambassadors.banner.dismiss": "Dismiss",

  // ── Ambassadors: WhatsApp share text ──
  "ambassadors.whatsapp.share_default":
    "I have been wearing Lecmo perfumes — quietly composed and quite unforgettable. Take a look: {url}",
  "ambassadors.whatsapp.share_product":
    "I think you would love this Lecmo creation — {title}. Have a look: {url}",

  // ── Lecmo AI Concierge ──
  "agent.fab_label": "Chat with Lecmo Concierge",
  "agent.title": "Lecmo Concierge",
  "agent.online": "Online now",
  "agent.welcome_bubble": "Hello ✨ I'm here to help — ask me anything about our perfumes.",
  "agent.welcome_bubble_close": "Close",
  "agent.greeting": "Welcome to Lecmo. How may I help you compose your perfect scent today?",
  "agent.placeholder": "Ask about perfumes, shipping, gifts…",
  "agent.send": "Send",
  "agent.close": "Close chat",
  "agent.clear_chat": "Clear chat",
  "agent.clear_chat_confirm": "Are you sure you want to clear the conversation?",
  "agent.clear_chat_yes": "Clear",
  "agent.clear_chat_no": "Cancel",
  "agent.suggestion_recommend": "Recommend a perfume",
  "agent.suggestion_gift": "How do I send a gift?",
  "agent.suggestion_track": "Where is my order?",
  "agent.error_generic": "Sorry, something went wrong. Please try again.",
  "agent.error_rate": "Too many messages. Please wait a moment.",
  "agent.error_credits": "AI service is temporarily unavailable.",
  "agent.error_too_long": "Message too long (max 500 characters).",
  "agent.action_whatsapp": "Talk on WhatsApp",
  "agent.toast_locale_changed": "Language updated",
  "agent.toast_market_changed": "Market changed to {country}",
  "common.retry": "Try again",
  "common.ok": "OK",
  "common.close": "Close",

  // ── Admin · Agent control panel ──
  // ── Thank you / order received ──
  "thankyou.meta.title": "Thank You — Lecmo",
  "thankyou.meta.description": "Your Lecmo order is confirmed — here is what happens next.",
  "thankyou.eyebrow": "Order received",
  "thankyou.title": "Thank you ✨",
  "thankyou.subtitle": "Your order has been confirmed and is being prepared with care.",
  "thankyou.order_number": "Order number",
  "thankyou.next_title": "What happens next",
  "thankyou.next_email": "We'll email you a confirmation shortly.",
  "thankyou.next_whatsapp": "We'll send WhatsApp updates when your order ships and is delivered.",
  "thankyou.next_track": "You can follow your order anytime from your account.",
  "thankyou.cta_track": "Track my order",
  "thankyou.cta_shop": "Continue shopping",
  "thankyou.cta_gift": "Send another as a gift",
  "thankyou.support":
    "Any question? Email {email} or message us on WhatsApp.",
  "thankyou.help_title": "Need help?",
  "thankyou.help_subtitle":
    "Our concierge answers within working hours — Sat to Thu, 10am–7pm (UAE).",
  "thankyou.help_whatsapp_title": "Chat with our concierge",
  "thankyou.help_whatsapp_desc": "Fastest reply, with your order details attached.",
  "thankyou.help_email_title": "Email us",
  "thankyou.help_email_desc": "Write to {email} and we'll get back to you.",
  "thankyou.help_track_title": "Track your order",
  "thankyou.help_track_desc": "Check the status anytime with your order number.",
  "thankyou.help_returns_title": "Returns & exchanges",
  "thankyou.help_returns_desc": "14 days from delivery, sealed and unused.",
  "thankyou.video_eyebrow": "A word from the house",
  "thankyou.video_title": "Thank you for choosing Lecmo",
  "thankyou.video_body": "A short note from us to you — with our gratitude.",

  // ── Ambassador own content (UGC) ──
  "ambassador.ugc.title": "Create your own content",
  "ambassador.ugc.intro":
    "Dear ambassador, you can film your own UGC video and post it with your personal link — every click and every purchase is credited to you automatically. Please respect the publishing rules below before you post.",
  "ambassador.ugc.step_product": "1. Pick a product (optional)",
  "ambassador.ugc.product_placeholder": "Search a product…",
  "ambassador.ugc.product_hint":
    "With a product selected, your link opens that product page. Without one, it opens the homepage.",
  "ambassador.ugc.preview_open": "Open product page",
  "ambassador.ugc.preview_clear": "Remove product",
  "ambassador.ugc.preview_save_image": "Save product image",
  "ambassador.ugc.preview_hint":
    "Use the product image or page as a reference while filming at home, and change the product anytime to switch your link.",

  "ambassador.ugc.your_link": "2. Your ready link",
  "ambassador.ugc.where_to_put_link":
    "Put the link in your bio, link sticker, or caption — not inside the video. Copy it from here for every post so your clicks are tracked.",
  "ambassador.ugc.step_caption": "3. Your own words",
  "ambassador.ugc.caption_placeholder":
    "Write freely in your own voice — just stay within the publishing rules below.",
  "ambassador.ugc.caption_free_hint":
    "This text is yours: no ready template. Say what you honestly felt, then paste your link.",
  "ambassador.ugc.platforms_hint":
    "Tap your platform: the caption + link are copied automatically, then the platform opens so you can paste.",
  "ambassador.ugc.caption_copied": "Caption + link copied",
  "ambassador.ugc.rules_title": "Video acceptance rules",
  "ambassador.ugc.rule1":
    "Respect public decency and local values — no explicit, vulgar, or suggestive content.",
  "ambassador.ugc.rule2": "No abuse, bullying, discrimination, or hate speech.",
  "ambassador.ugc.rule3": "No political, religious, or controversial content.",
  "ambassador.ugc.rule4":
    "No medical or therapeutic claims about fragrances, and no false result promises.",
  "ambassador.ugc.rule5":
    "No prices, discounts, or offers that Lecmo has not announced officially.",
  "ambassador.ugc.rule6":
    "No disparaging comparisons with other brands, and no impersonating Lecmo or its team.",
  "ambassador.ugc.rule7":
    "Respect IP rights: licensed music and clips, and consent from everyone appearing in the video.",
  "ambassador.ugc.rule8":
    "No bought followers or clicks, no automation, spam, or click-exchange groups.",
  "ambassador.ugc.rule9":
    "Disclose the commercial relationship (#ad or “in partnership with Lecmo”) per your market's rules.",
  "ambassador.ugc.rule10":
    "Protect the brand image: clean image and audio, and the product shown respectfully.",
  "ambassador.ugc.enforcement":
    "Breaking any rule gives Lecmo the right to disable the ambassador's link and code, cancel unpaid commissions, and request removal of the post, without prior notice.",
  "ambassador.ugc.consent": "I have read and agree to the publishing rules.",
  "ambassador.ugc.consent_required": "Accept the rules to enable copying and sharing.",



  // ── Wishlist reminder (WhatsApp + email body for wishlist_only segment) ──
  "wishlist_reminder.whatsapp.single":
    "Hi {name} 🌙 You saved “{product}” in your Lecmo wishlist a few days ago. Here's a small gift to bring it home: code {code} for {discount}% off, valid {days} days. {url}",
  "wishlist_reminder.whatsapp.multi":
    "Hi {name} 🌙 You have {count} fragrances waiting in your Lecmo wishlist. Here's {discount}% off to start your collection: code {code}, valid {days} days. {url}",
  "wishlist_reminder.email.subject": "Your Lecmo wishlist is waiting — {discount}% inside",
  "wishlist_reminder.email.heading": "Your wishlist is patient. Your scent shouldn't be.",
  "wishlist_reminder.email.body":
    "We noticed {count} fragrance(s) you liked are still in your wishlist. Use code {code} at checkout for {discount}% off — valid {days} days.",
  "wishlist_reminder.email.cta": "Open my wishlist",

  // ── Ambassador relationship types & tiers (display labels) ──
  "ambassador.relationship.organic": "Organic",
  "ambassador.relationship.signature_partner": "Signature Partner",
  "ambassador.relationship.vip_guest": "VIP Guest",
  "ambassador.tier.decouverte": "Discovery",
  "ambassador.tier.active": "Signature",
  "ambassador.tier.elite": "Maison",

  // ── FAQ ──
  "faq.eyebrow": "Help Center",
  "faq.title": "Frequently asked",
  "faq.subtitle": "The questions we hear most. If yours isn't here, reach us on WhatsApp — we reply within minutes.",
  "faq.cta_contact": "Still need help? Talk to us",
  "faq.meta.title": "FAQ — Lecmo | Orders, Shipping & Fragrance Help",
  "faq.meta.description": "Answers about Lecmo orders, shipping, gifting, returns, and choosing the right fragrance.",

  "faq.cat_orders": "Orders & Shipping",
  "faq.q.shipping_time.q": "How long does delivery take?",
  "faq.q.shipping_time.a": "UAE, Qatar, Oman, Greater Riyadh and Baghdad: orders before noon local time usually arrive the same day; at or after noon, the next day. Elsewhere in Saudi Arabia and Iraq: 1–3 days. International delivery: 7 days expected. We deliver every day; times are estimates.",
  "faq.q.shipping_cost.q": "How much is shipping?",
  "faq.q.shipping_cost.a": "Free in the UAE, Oman and Qatar. Saudi Arabia: SAR 100 up to and including SAR 1,000; free above SAR 1,000. Kuwait and Bahrain: AED 100 below AED 1,000; free at AED 1,000 or more. Iraq: USD 10 per order. For the US and covered international destinations listed on our Shipping page: USD 125 below USD 350; free at USD 350 or more, based on the eligible subtotal after discounts and before taxes. For other destinations, contact us before ordering to confirm availability and charges.",
  "faq.q.cod.q": "Do you accept cash on delivery?",
  "faq.q.cod.a": "Yes, in the markets where it is available. COD is not available for gift orders — gifts are always card-paid so the recipient is never asked to pay.",
  "faq.q.track.q": "How do I track my order?",
  "faq.q.track.a": "From your account under \"My orders\", or from the tracking link we send by SMS, email, and WhatsApp.",

  "faq.cat_returns": "Returns & Authenticity",
  "faq.q.returns.q": "Can I return a perfume?",
  "faq.q.returns.a": "Perfumes are personal and intimate, so we do not accept returns of fragrances under any circumstance — except when the fault is ours: a damaged bottle, the wrong fragrance, or a missing item. Report it within 48 hours of delivery and we'll fix it at our cost.",
  "faq.q.damaged.q": "I received a product that doesn't match my order — what do I do?",
  "faq.q.damaged.a": "Send us a photo on WhatsApp within 48 hours. We'll arrange a replacement, refund, or store credit — whichever you prefer — and cover all return shipping.",

  "faq.cat_payment": "Payment & Currency",
  "faq.q.currency.q": "What currency do I pay in?",
  "faq.q.currency.a": "Your local currency for the six supported markets: AED (UAE), SAR (Saudi Arabia), OMR (Oman), USD (Iraq, United States, and rest of world), BRL (Brazil). The price you see at checkout is exactly what you pay.",
  "faq.q.cards.q": "Which payment methods do you accept?",
  "faq.q.cards.a": "All major credit and debit cards through our secure payment processor, plus cash on delivery in supported markets and Apple Pay where available.",

  "faq.cat_gifts": "Gifts",
  "faq.q.gift.q": "Can I send a gift to someone in another country?",
  "faq.q.gift.a": "Yes — choose the recipient's country at checkout. We fulfil locally in the UAE, Saudi Arabia, Oman, Qatar and Iraq. Delivery is free in the UAE, Oman and Qatar; Iraq costs USD 10 per order. Saudi Arabia: SAR 100 up to and including SAR 1,000; free above SAR 1,000. Kuwait and Bahrain: AED 100 below AED 1,000; free at AED 1,000 or more. For the US and covered international destinations on our Shipping page: USD 125 below USD 350; free at USD 350 or more, based on the eligible subtotal after discounts and before taxes. Contact us before ordering to confirm availability and charges for other destinations. The recipient never sees the price, and you receive a notification when they receive it.",
  "faq.q.gift_card.q": "Do you include a card with the gift?",
  "faq.q.gift_card.a": "Yes. You can write a personal message that we hand-write on a Lecmo card and place inside the box. Internal delivery instructions stay between you and our team.",

  "faq.cat_account": "Account & Loyalty",
  "faq.q.loyalty.q": "How does the loyalty program work?",
  "faq.q.loyalty.a": "Every order earns you points that can be redeemed against future purchases. The more you order, the higher your tier — Lecmo Lover, Lecmo Ambassador, then Super Star Ambassador. See your tier and points anytime in your account.",
  "faq.q.ambassador.q": "How do I become an ambassador?",
  "faq.q.ambassador.a": "Apply through your account. We review every application by hand. Ambassadors earn a commission on every order placed through their personal link, redeemed as follows: An instant store card usable at checkout or cashable for its value, and loyalty points redeemed into a store coupon. The payout channels available to you — including bank transfer — are shown in your ambassador dashboard.",

  // Ambassador install page (/account/install-app)
  "install.page.meta_title": "Install the Lecmo app — Ambassadors",
  "install.page.title": "Install Lecmo on your phone",
  "install.page.intro": "Pin Lecmo to your home screen and share faster, track clicks in one tap, and look professional to your audience.",
  "install.page.why": "Why install?",
  "install.page.why_1": "One tap to open — no browser bar, looks like a native app.",
  "install.page.why_2": "Faster loading and push-style notifications for new gifts.",
  "install.page.why_3": "Your ambassador library, links, and wallet always one tap away.",
  "install.page.android_title": "Install on Android",
  "install.page.android_step1_title": "1. Open in Chrome",
  "install.page.android_step1_body": "If you opened this from Instagram, WhatsApp or another app, tap the «⋮» menu and choose «Open in Chrome» first.",
  "install.page.android_step2_title": "2. Tap «Install app»",
  "install.page.android_step2_body": "Use the button above, or tap the «⋮» menu in Chrome and choose «Install app» / «Add to Home screen».",
  "install.page.android_step3_title": "3. Confirm «Install»",
  "install.page.android_step3_body": "A small dialog appears — tap «Install». The Lecmo icon will appear on your home screen like any other app.",
  "install.page.ios_title": "Install on iPhone",
  "install.page.trouble_title": "Not seeing the install button?",
  "install.page.trouble_1": "Open lecmo.com directly in Safari (iPhone) or Chrome (Android), not inside Instagram/Facebook/TikTok browser.",
  "install.page.trouble_2": "If you already installed it once, the button is hidden — check your home screen.",
  "install.page.trouble_3": "Use the kebab menu «⋮» on Android or the Share icon on iPhone if the prompt did not appear.",
  "install.page.share_title": "Share with your audience",
  "install.page.share_body": "Send these steps to anyone who finds installation tricky.",
  "install.page.share_cta": "Copy install link",
  "install.page.share_copied": "Link copied",
  "install.page.back": "Back to ambassador dashboard",

  // ── Ambassadors: Terms & Conditions page ──
  "account.ambassador.terms_invite.title": "One small step — accept the Ambassador Terms",
  "account.ambassador.terms_invite.body": "You have earned your first referral commission. Please review and accept the Ambassador Terms so we can pay it out to you.",
  "account.ambassador.terms_invite.note": "Your earnings are safe: nothing is cancelled or changed. Acceptance is only needed before a cash withdrawal or turning referral earnings into a coupon.",
  "account.ambassador.terms_invite.gate_error": "Please accept the Ambassador Terms first — open the Ambassador Program terms page from your dashboard.",
  "ambassadors.terms.eyebrow": "Ambassador Program",
  "ambassadors.terms.title": "Terms & Conditions",
  "ambassadors.terms.subtitle": "The rules of the Lecmo Ambassador Program — written in plain language, kept up to date.",
  "ambassadors.terms.last_updated": "Current version",
  "ambassadors.terms.toc": "Sections",
  "ambassadors.terms.accept_cta": "I have read and agree to the Ambassador Terms",
  "ambassadors.terms.accept_btn": "Accept terms",
  "ambassadors.terms.already_accepted": "You accepted the current version on {date}.",
  "ambassadors.terms.needs_accept_banner": "A newer version of the terms is in effect. Please review and accept to continue using ambassador features (including cash withdrawals).",
  "ambassadors.terms.accepted_toast": "Thank you — your acceptance has been recorded.",
  "ambassadors.terms.error_toast": "We couldn't save your acceptance. Please try again.",
  "ambassadors.terms.signin_required": "Sign in to record your acceptance.",
  "ambassadors.terms.back_to_program": "Back to the Ambassador Program",
  "ambassadors.form.terms_agree": "I have read and agree to the",
  "ambassadors.form.terms_link": "Ambassador Program Terms",
  "ambassadors.form.terms_required": "Please agree to the Ambassador Program Terms first.",

  "ambassadors.terms.s1.title": "1. Introduction",
  "ambassadors.terms.s1.body": "Every Lecmo customer is automatically enrolled in the Ambassador Program from the moment they create an account. By using the program you confirm that you have read, understood, and agreed to these Terms. The program is operated by Maison Lecmo (\"we\", \"us\"), and these Terms govern the relationship between you (\"the Ambassador\") and Maison Lecmo with respect to the program only — they do not replace our general Terms of Service, Privacy Policy, or Shipping & Returns policies, which continue to apply.",

  "ambassadors.terms.s2.title": "2. Commissions & Rewards",
  "ambassadors.terms.s2.body": "You earn a commission on every qualified purchase made through your personal ambassador code or link, in accordance with your current tier. Commission rates, qualifying conditions, and the value of any loyalty points are published in your account dashboard at /account/ambassador and may be updated from time to time. Commissions are calculated on the net product subtotal of the order (excluding taxes, shipping, gift wrapping, discounts, and any amount paid using store credit, gift cards, or loyalty redemptions). Commissions are credited as pending and become available after the order's return window has closed and any refund or chargeback period has expired.",

  "ambassadors.terms.s3.title": "3. Tiers & Promotions",
  "ambassadors.terms.s3.body": "The program has multiple tiers (Lecmo Lover, Lecmo Ambassador, Super Star Ambassador). Tier eligibility is reviewed automatically based on activity criteria (sales, referrals, milestones) published in your dashboard. Promotion may be automatic or subject to admin review depending on the tier. We reserve the right to revoke a promotion if the qualifying activity is later reversed (for example, due to refunds, chargebacks, or fraud). Your accumulated commissions and points are not affected by tier downgrades unless explicitly stated in the rule that applied at the time they were earned.",

  "ambassadors.terms.s4.title": "4. Withdrawals & Payouts",
  "ambassadors.terms.s4.body": "Cash payouts are subject to a minimum withdrawal threshold, a daily cap, and a processing window — all published in your wallet at /account/ambassador and configurable by Maison Lecmo. You must register a valid bank account in your name before requesting any payout. Withdrawals to accounts not in your legal name will be rejected and may lead to suspension. You authorize us to verify your identity, address, and banking details, and to delay or refuse a payout if we have reasonable grounds to suspect fraud, abuse, or breach of these Terms. You acknowledge that taxes on amounts received from the program are your sole responsibility under the laws of your country of residence.",

  "ambassadors.terms.s5.title": "5. Milestones",
  "ambassadors.terms.s5.body": "From time to time we publish milestone rewards (bonus cash, free product, or status upgrades) tied to specific achievements. The list of active milestones, their conditions, and their rewards is published in your dashboard. Each milestone can be completed once per ambassador unless stated otherwise. We may add, modify, or end milestones at any time; changes do not retroactively cancel a milestone you have already completed.",

  "ambassadors.terms.s6.title": "6. Refunds, Cancellations & Reversals",
  "ambassadors.terms.s6.body": "If a customer cancels or returns an order on which you earned a commission, the corresponding amount will be reversed from your wallet. If the reversed amount exceeds your available balance, the difference is recorded as a negative balance to be offset against future earnings. Chargebacks, payment fraud, and orders flagged as suspicious follow the same rule. Coupons generated from your wallet that expire unused are automatically released back to your available balance.",

  "ambassadors.terms.s7.title": "7. Content Rights & Conduct",
  "ambassadors.terms.s7.body": "You retain ownership of any content you create about Lecmo. By submitting content to your sharing library, you grant Maison Lecmo a non-exclusive, royalty-free, worldwide license to use, display, and reproduce it for the purpose of operating and promoting the Ambassador Program. You agree not to: (a) misrepresent Lecmo, its products, or its values; (b) make medical, therapeutic, or unverifiable claims about our products; (c) use paid search ads on the brand name \"Lecmo\" or any obvious variant; (d) buy from your own code, or coordinate self-purchases through close family; (e) spam, mass-message, or harvest contacts to distribute your code; (f) engage in any conduct that misleads consumers or violates applicable consumer-protection law.",

  "ambassadors.terms.s8.title": "8. Termination",
  "ambassadors.terms.s8.body": "You may leave the program at any time by contacting us. We may suspend or terminate your participation, withhold pending commissions, and reverse paid commissions if we determine that you have materially breached these Terms, engaged in fraudulent or abusive activity, or harmed the reputation of Maison Lecmo. Termination does not affect commissions already earned and cleared in good faith, except where they relate to the breach itself.",

  "ambassadors.terms.s9.title": "9. Privacy & Data",
  "ambassadors.terms.s9.body": "We process personal data (name, contact details, country, banking information, link clicks, and sales attributed to your code) for the sole purpose of operating the program, paying you, complying with our legal obligations, and protecting against fraud. Banking details are encrypted at rest. You may request a copy or deletion of your personal data at any time, subject to legal retention obligations. Full details are available in our Privacy Policy.",

  "ambassadors.terms.s10.title": "10. Changes to These Terms",
  "ambassadors.terms.s10.body": "We may update these Terms when the rules of the program change. Any change applies only to commissions, points, coupons, and milestones generated after the change takes effect — amounts already earned or held under a previous version are honored under the rules that applied when they were created. When the version changes you will be asked to review and accept the new version before requesting your next cash withdrawal. Refusal to accept a new version stops cash withdrawals but does not delete your existing balance.",

  "ambassadors.terms.s11.title": "11. Countries Counted for Click Earnings",
  "ambassadors.terms.s11.body": "Click earnings, loyalty points, and any per-click reward are credited only for visits coming from the countries taking part in the active campaign. Unless a campaign states otherwise, these are Lecmo's served markets: the United Arab Emirates, Saudi Arabia, Oman, and Iraq. Please review the list of participating countries for the campaign before you share your link. Maison Lecmo may add or remove countries from this list; any change is announced to ambassadors beforehand through the ambassador dashboard and notifications, and applies only to clicks recorded after the announcement — clicks already credited are not affected. Clicks from countries outside the list do not create any entitlement to points, cash, coupons, or status progress.",

  "ambassadors.terms.s12.title": "12. Your Agreement Is With Maison Lecmo",
  "ambassadors.terms.s12.body": "This agreement is between you and Maison Lecmo only. You may have joined through an external affiliate platform (for example UpPromote) or a partner channel; such platforms are sign-up and tracking channels used by Maison Lecmo and are not a party to this agreement, not your employer, and not a paying entity. Maison Lecmo alone calculates your commissions, points, coupons, and tier progress, and executes every payout from your Lecmo wallet under the rules in these Terms. Where an external platform's dashboard shows figures that differ from ours, Lecmo's own records are the binding reference. You are not entitled to a second payment for the same click, order, or activity from any other platform. Registering through an external channel does not by itself create any entitlement: your account must be approved in Lecmo and you must accept the current version of these Terms before rewards accrue or a withdrawal can be requested.",

  // ── Phone messages (gate dialog + validation errors) ──
  "phone.gate.title": "One step to complete your profile",
  "phone.gate.desc": "We need your mobile number to confirm orders and shipping updates. We won't use it for marketing without your consent.",
  "phone.gate.privacy": "Your number is protected and visible only to our team when processing your order.",
  "phone.gate.cta_save": "Save and continue",
  "phone.gate.cta_saving": "Saving…",
  "phone.gate.cta_later": "Remind me later",
  "phone.gate.success": "Phone saved ✓",
  "phone.gate.error_already_linked": "This phone is linked to another account.",
  "phone.gate.error_save_failed": "Couldn't save — try again",
  "phone.error.empty": "Enter your mobile number.",
  "phone.error.invalid_format": "Invalid format. Example: +9715XXXXXXXX",
  "phone.error.country_unsupported": "Country code not supported. Pick a country from the list.",
  "phone.error.national_length": "{country} numbers must be {n} digits after the country code.",
  // ── About Us (/about) — الصفحة التحريريّة الجديدة ──
  "aboutus.meta.title": "About Lecmo | An Emirati House of Modern Perfumery",
  "aboutus.meta.description":
    "Lecmo is an Emirati house of modern perfumery — learn about our origins, philosophy, and the people behind the scents.",
  "aboutus.hero.eyebrow": "ABOUT LECMO",
  "aboutus.hero.title": "Fragrance, Felt Beyond the Scent",
  "aboutus.hero.body":
    "LECMO is an independent perfume house born in the UAE, creating fragrances and gifting experiences shaped by emotion, character, and thoughtful detail.",
  "aboutus.hero.cta": "Discover Our Fragrances",
  "aboutus.hero.image_alt": "A LECMO fragrance bottle photographed in warm, quiet light",
  "aboutus.story.eyebrow": "OUR STORY",
  "aboutus.story.title": "Where Taste Met Emotion",
  "aboutus.story.p1":
    "LECMO began in the UAE in 2017 with a simple belief: fragrance should be more than something we wear. It should hold a feeling, express a character, and remain in memory long after the moment has passed.",
  "aboutus.story.p2":
    "From the composition of each scent to the shape of every bottle, bag, and gift box, we approach fragrance as a complete experience. Every detail is considered to make discovering, wearing, and gifting LECMO feel personal.",
  "aboutus.story.image_alt": "Close detail of LECMO craftsmanship — bottle, cap and packaging",
  "aboutus.quote.text": "A brand that whispers, yet everyone hears its presence.",
  "aboutus.philosophy.eyebrow": "OUR PHILOSOPHY",
  "aboutus.philosophy.1.title": "Emotion",
  "aboutus.philosophy.1.body":
    "We create fragrances that connect with feelings, memories, and individual character.",
  "aboutus.philosophy.2.title": "Thoughtful Detail",
  "aboutus.philosophy.2.body":
    "From the first note to the final touch of packaging, every element belongs to one complete experience.",
  "aboutus.philosophy.3.title": "Quiet Confidence",
  "aboutus.philosophy.3.body":
    "Our idea of luxury is expressive without excess, distinctive without needing to be loud.",
  "aboutus.exp.eyebrow": "THE LECMO EXPERIENCE",
  "aboutus.exp.title": "More Than a Bottle",
  "aboutus.exp.body":
    "A LECMO fragrance is designed to be discovered in stages—from the first visual impression and the opening notes to the way it settles on the skin and remains in memory. Our collections bring perfume, presentation, and gifting together in one considered experience.",
  "aboutus.exp.link": "Explore the Collections",
  "aboutus.exp.image_alt": "LECMO presentation — fragrance, bag and gift set details",
  "aboutus.uae.eyebrow": "FROM THE UAE TO THE WORLD",
  "aboutus.uae.title": "Born in the UAE. Created for the World.",
  "aboutus.uae.body":
    "Rooted in the UAE and inspired by diverse tastes and stories, LECMO continues to grow with a vision of sharing its fragrance experience across the region and beyond.",
  "aboutus.uae.image_alt": "Quiet desert light, an understated reference to LECMO's Emirati origin",
  "aboutus.final.title": "Find the Fragrance That Feels Like You",
  "aboutus.final.body":
    "Explore the world of LECMO and discover a scent that speaks in your own language.",
  "aboutus.final.primary": "Shop Fragrances",
  "aboutus.final.image_alt": "A LECMO collection arranged for a closing editorial image",
};


// ─────────────────────────────────────────────
// AR
// ─────────────────────────────────────────────
// القاموس العربيّ نُقل إلى i18n.content.ar.ts ويُحمَّل ديناميكياً عبر
// `loadLocaleContent("ar")` في i18n.ts — أوّل رسم إنجليزيّ دائماً فلا وميض.
// السبب: إخراج ~150KB من الحزمة الأساسيّة (زمن تنفيذ الجوّال).

// ─────────────────────────────────────────────
// FR
// ─────────────────────────────────────────────

// الفرنسيّة أُرشِفت في i18n.content.fr.archive.ts (غير محزَّمة للزائر).
// fr يشير إلى en كاحتياط آمن للأنواع فقط.
export const CONTENT_DICTS: Record<Locale, Dict> = { en, ar: {
  "driver.delivery_event_retry": "إعادة تسوية حدث التسليم",
  "driver.delivery_event_result": "حدث التسليم: {result}",
  "driver.login.title": "دخول السائق",
  "driver.login.email": "البريد الإلكتروني",
  "driver.login.password": "كلمة المرور",
  "driver.login.submit": "دخول",
  "driver.login.error": "تعذّر الدخول. تحقق من بياناتك والاتصال.",
  "recovery.email_greeting": "مرحباً،",
  "recovery.email_selection": "اختيارك",
}, fr: en };
